import React from 'react';
import { getApiUrl } from '../../lib/getApiUrl';
import { useAuthFetch } from '../../lib/useAuthFetch';

interface Bid {
  id: string;
  contractorId?: string;
  contractor?: {
    user?: {
      email?: string;
    };
  };
}

interface AvailableProject {
  id: string;
  address: string;
  projectType?: string;
  status: string;
  bids?: Bid[];
  roofReportUrl?: string;
  computedDistanceMiles?: number | null;
  projectLat?: number | null;
  projectLng?: number | null;
}

interface AvailableProjectCardProps {
  project: AvailableProject;
  hasBidded: boolean;
  isMissingReport: boolean;
  /** When true, show "View Roof Report" button (list API has roofReportUploadedAt but not roofReportUrl) */
  hasRoofReport?: boolean;
  adminView?: boolean;
  onSubmitBid: (projectId: string) => void;
  onShowBidDetails: (projectId: string, bid: Bid) => void;
}

/**
 * Displays a single available project card in the contractor dashboard
 * Handles bidding state, roof report availability, and visual styling
 */
export default function AvailableProjectCard({
  project,
  hasBidded,
  isMissingReport,
  hasRoofReport: hasRoofReportProp,
  adminView = false,
  onSubmitBid,
  onShowBidDetails,
}: AvailableProjectCardProps) {
  const hasRoofReport = hasRoofReportProp ?? !!project.roofReportUrl;
  const authFetch = useAuthFetch();

  return (
    <div
      key={project.id}
      className="project-card"
      style={{
        marginBottom: '16px',
        padding: '20px',
        border: isMissingReport
          ? '2px solid #fca5a5'
          : hasBidded
          ? '2px solid #f97316'
          : '1px solid #e5e7eb',
        borderRadius: '8px',
        background: isMissingReport
          ? '#fef2f2'
          : hasBidded
          ? '#fff7ed'
          : 'white',
        boxShadow: isMissingReport
          ? '0 2px 8px rgba(239, 68, 68, 0.15)'
          : hasBidded
          ? '0 2px 8px rgba(249, 115, 22, 0.2)'
          : '0 1px 3px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease',
        opacity: isMissingReport && !adminView ? 0.8 : 1,
        ...(adminView ? { pointerEvents: 'none' } : {})
      }}
      onMouseEnter={(e) => {
        if (!adminView && !isMissingReport) {
          e.currentTarget.style.borderColor = hasBidded ? '#f97316' : '#2563eb';
          e.currentTarget.style.boxShadow = hasBidded
            ? '0 4px 12px rgba(249, 115, 22, 0.25)'
            : '0 4px 12px rgba(37, 99, 235, 0.15)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!adminView && !isMissingReport) {
          e.currentTarget.style.borderColor = hasBidded ? '#f97316' : '#e5e7eb';
          e.currentTarget.style.boxShadow = hasBidded
            ? '0 2px 8px rgba(249, 115, 22, 0.2)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
        } else if (!adminView && isMissingReport) {
          e.currentTarget.style.borderColor = '#fca5a5';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.15)';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px', color: isMissingReport ? '#dc2626' : hasBidded ? '#c2410c' : '#2563eb' }}>
            {project.address || 'Address TBD'}
          </h3>
          {project.computedDistanceMiles != null && (
            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>
              ({Math.max(0.1, project.computedDistanceMiles).toFixed(1)} miles from you)
            </p>
          )}
          <p style={{ color: '#6b7280', marginBottom: '8px' }}>
            {project.projectType || 'Residential'}
          </p>

          {/* Roof Report Indicator */}
          {isMissingReport && (
            <div style={{
              marginTop: '8px',
              padding: '8px 12px',
              borderRadius: '6px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              display: 'inline-block',
              marginBottom: '12px'
            }}>
              <span style={{ color: '#dc2626', fontSize: '13px', fontWeight: '600' }}>
                ⚠️ Missing Roof Report - Bidding Disabled Until Admin Adds Report
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <button
              disabled={adminView || isMissingReport}
              onClick={() => {
                if (adminView || isMissingReport) return;
                if (hasBidded) {
                  const contractorBid = project.bids?.find(b => b.contractorId);
                  if (contractorBid) onShowBidDetails(project.id, contractorBid);
                } else {
                  onSubmitBid(project.id);
                }
              }}
              style={{
                height: 36,
                padding: '0 18px',
                borderRadius: 8,
                border: 'none',
                fontSize: 14,
                fontWeight: 600,
                cursor: isMissingReport || adminView ? 'not-allowed' : 'pointer',
                backgroundColor: isMissingReport ? '#e5e7eb' : hasBidded ? '#f97316' : '#2563eb',
                color: isMissingReport ? '#9ca3af' : 'white',
                opacity: isMissingReport ? 0.7 : 1,
                whiteSpace: 'nowrap' as const,
              }}
            >
              {hasBidded ? 'Review Bid' : isMissingReport ? 'Bid Unavailable' : 'Submit Bid'}
            </button>
            {hasRoofReport && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const apiUrl = getApiUrl(`/api/projects/${project.id}/roof-report/download?format=json`);
                    const res = await authFetch(apiUrl);
                    if (!res.ok) { alert('Failed to load roof report'); return; }
                    const data = await res.json();
                    if (data.downloadUrl) { window.open(data.downloadUrl, '_blank', 'noopener,noreferrer'); }
                    else { alert('Report not available'); }
                  } catch {
                    alert('Failed to load roof report');
                  }
                }}
                style={{
                  height: 36,
                  padding: '0 18px',
                  borderRadius: 8,
                  border: '1.5px solid #10b981',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  color: '#059669',
                  whiteSpace: 'nowrap' as const,
                }}
              >
                View Report
              </button>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', marginLeft: '16px', flexShrink: 0 }}>
          {hasBidded ? (
            <span style={{
              display: 'inline-block',
              background: '#f97316',
              color: 'white',
              fontSize: '12px',
              fontWeight: '700',
              padding: '4px 10px',
              borderRadius: '20px',
              whiteSpace: 'nowrap',
              letterSpacing: '0.03em',
            }}>
              ✓ BID SUBMITTED
            </span>
          ) : (
            <span className="status-badge status-pending" style={{ fontSize: '12px', padding: '2px 8px', display: 'block', textAlign: 'center' }}>
              {project.bids?.length || 0} BID{project.bids?.length !== 1 ? 'S' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
